import { AudioPresets, VideoQuality, type TrackPublishOptions } from 'livekit-client';
import type { MediaProfile } from '@shared/protocol';

export interface ProfileSettings {
  /** Size and frame rate for getUserMedia. The device id is added separately. */
  video: MediaTrackConstraints;
  /** Microphone processing for getUserMedia. Device id added separately. */
  audio: MediaTrackConstraints;
  publish: TrackPublishOptions;
  /** Ceiling on what this participant asks the SFU to send them, applied on top
      of adaptive stream's own sizing. Only set where it earns its keep. */
  maxReceive?: VideoQuality;
}

/**
 * The browser's voice pipeline: echo cancellation, noise suppression, gain
 * control, and Chrome's machine-learning `voiceIsolation` on top. Together they
 * are what makes a laptop microphone in a live room sound like a conference
 * product. They also downmix to mono and mangle anything that is not speech,
 * which is why the studio profile turns the lot off.
 */
const VOICE_DSP: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  voiceIsolation: true,
  channelCount: { ideal: 1 },
} as MediaTrackConstraints;

/** Every profile publishes VP9. VP8 simulcast runs three independent encoders
    and pays for all three; VP9's spatial layers come out of one pass, and it is
    sharper per bit besides. `backupCodec` adds a VP8 stream, but only if
    someone joins who cannot decode VP9. */
const CODEC = { videoCodec: 'vp9', backupCodec: true } as const;

export const PROFILES: Record<MediaProfile, ProfileSettings> = {
  /* Audio is the part of a call you cannot do without, so on a starved link it
     gets the priority and the picture takes what is left. The receive ceiling
     matters as much as the send one: four people at 720p arriving over a phone
     tether is the same problem from the other direction. */
  low: {
    video: { width: { ideal: 640 }, height: { ideal: 360 }, frameRate: { ideal: 20 } },
    audio: VOICE_DSP,
    publish: {
      ...CODEC,
      videoEncoding: { maxBitrate: 350_000, maxFramerate: 20, priority: 'low' },
      degradationPreference: 'balanced',
      audioPreset: AudioPresets.speech,
      dtx: true,
      red: true,
    },
    maxReceive: VideoQuality.LOW,
  },

  meeting: {
    video: { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, min: 20 } },
    audio: VOICE_DSP,
    publish: {
      ...CODEC,
      videoEncoding: { maxBitrate: 2_500_000, maxFramerate: 30, priority: 'high' },
      /* The browser's default for a camera is 'maintain-framerate', which
         answers congestion by shrinking the picture and never steps back up.
         For a talking head, hold the resolution and drop frames instead. */
      degradationPreference: 'maintain-resolution',
      audioPreset: AudioPresets.music,
      dtx: true,
      red: true,
    },
  },

  'hq-video': {
    video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30, min: 24 } },
    audio: VOICE_DSP,
    publish: {
      ...CODEC,
      videoEncoding: { maxBitrate: 6_000_000, maxFramerate: 30, priority: 'high' },
      /* 'balanced' rather than the meeting profile's maintain-resolution: this
         one is pointed at something moving, and 1080p at five frames a second
         is not a better answer than 720p at thirty. */
      degradationPreference: 'balanced',
      audioPreset: AudioPresets.music,
      dtx: true,
      red: true,
    },
  },

  studio: {
    video: { width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30, min: 24 } },
    /* Every one of these off, deliberately. Echo cancellation forces a mono
       downmix, noise suppression hears a held note as noise, and gain control
       flattens the dynamics out of a performance. The cost is that open
       speakers will feed back — the lobby says so. */
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      voiceIsolation: false,
      channelCount: { ideal: 2 },
    } as MediaTrackConstraints,
    publish: {
      ...CODEC,
      videoEncoding: { maxBitrate: 6_000_000, maxFramerate: 30, priority: 'high' },
      degradationPreference: 'balanced',
      audioPreset: AudioPresets.musicHighQualityStereo,
      forceStereo: true,
      /* Both are speech tricks that cost fidelity. DTX cuts the stream during
         "silence", which swallows reverb tails and quiet passages; RED spends
         double the audio bitrate on redundancy. LiveKit disables them for
         stereo anyway — stated here so it reads as a choice. */
      dtx: false,
      red: false,
    },
  },
};

export function settingsFor(profile: MediaProfile | undefined): ProfileSettings {
  return PROFILES[profile ?? 'meeting'] ?? PROFILES.meeting;
}
